import type { AppendMessage } from '@assistant-ui/react'
import { uniq } from 'es-toolkit'

import {
  LocatorParseError,
  parseLocator,
} from '@/components/chat-v2/lib/locator'
import { canonicalizeSelectionLocator } from '@/components/chat-v2/lib/selectionLocator'
import type { PastedTextCardSource } from '@/components/chat-v2/providers/ChatEnvironmentProvider'

import type { UUID } from '@/types/Id'
import {
  type PlaysCitation,
  playsCitationKey,
} from '@/types/library/PlaybookRef'

export const PASTED_TEXT_PASTE_THRESHOLD_CHARS = 500
const PASTED_TEXT_CARD_TITLE_MAX_CHARS = 56

interface PastedTextMergeCard {
  source: PastedTextCardSource
  title: string
  text: string
  location?: { fileId: UUID; locator?: string | null } | null
  plays?: PlaysCitation | null
}

/**
 * A card that rides inline as a chip, in the composer's input and at the head
 * of the sent bubble: a selection of a file or plays, or a quote of an answer
 * being replied to. The others sit as blocks above it.
 */
export const isInlineCard = (card: { source: PastedTextCardSource }) =>
  card.source === 'selection' || card.source === 'reply'

export const createPastedTextCardTitle = (text: string) => {
  const singleLine = text.replace(/\s+/g, ' ').trim()
  if (!singleLine) return 'Pasted text'

  if (singleLine.length <= PASTED_TEXT_CARD_TITLE_MAX_CHARS) {
    return singleLine
  }
  return `${singleLine.slice(0, PASTED_TEXT_CARD_TITLE_MAX_CHARS - 1)}…`
}

// The label a card's header names its source by. Both header regexes are
// built from these, so a label added here is escaped as well as parsed.
const CARD_SOURCE_LABELS: Record<PastedTextMergeCard['source'], string> = {
  paste: 'Pasted text',
  playbook: 'Playbook',
  selection: 'Selection',
  reply: 'Reply',
}
const CARD_LABEL_PATTERN = Object.values(CARD_SOURCE_LABELS).join('|')
const sourceOfLabel = (label: string): PastedTextMergeCard['source'] =>
  (Object.keys(CARD_SOURCE_LABELS) as PastedTextMergeCard['source'][]).find(
    (source) => CARD_SOURCE_LABELS[source] === label,
  ) ?? 'paste'

// A line of typed or card text shaped like a header (or an escaped one)
// travels with one more leading backslash, so it never reads back as a header.
const HEADER_SHAPED_LINE_REGEX = new RegExp(
  `^\\\\*\\[(?:${CARD_LABEL_PATTERN}) \\d+: .*\\]$`,
)

export const escapeHeaderShapedLines = (text: string) =>
  text
    .split('\n')
    .map((line) => (HEADER_SHAPED_LINE_REGEX.test(line) ? `\\${line}` : line))
    .join('\n')

const unescapeHeaderShapedLine = (line: string) =>
  line.startsWith('\\') && HEADER_SHAPED_LINE_REGEX.test(line)
    ? line.slice(1)
    : line

/** The cards as the message carries them: a header naming each, then its text. */
export const formatPastedTextCards = (cards: PastedTextMergeCard[]) =>
  cards
    .map((card, index) => {
      const sourceLabel = CARD_SOURCE_LABELS[card.source]
      // The message text carries only canonical citation locators — chat-only
      // selection forms (`pdf:bbox`, `txt:plain`) are downgraded so the agent
      // never learns a vocabulary markit cannot resolve.
      const canonicalLocator =
        card.source === 'selection' && card.location?.locator
          ? canonicalizeSelectionLocator(card.location.locator)
          : null
      const locationSuffix =
        card.source !== 'selection'
          ? ''
          : card.plays
            ? ` @ ${playsCitationKey(card.plays)}`
            : card.location
              ? `${canonicalLocator ? ` @ ${canonicalLocator}` : ''} @ file:${card.location.fileId}`
              : ''
      return `[${sourceLabel} ${index + 1}: ${card.title}${locationSuffix}]\n${escapeHeaderShapedLines(card.text)}`
    })
    .join('\n\n')

export const mergePastedTextCardsIntoMessage = (
  message: AppendMessage,
  cards: PastedTextMergeCard[],
): AppendMessage => {
  if (cards.length === 0) return message

  const pastedTextBlock = formatPastedTextCards(cards)

  const nextContent = [...message.content]
  const textPartIndex = nextContent.findIndex((part) => part.type === 'text')

  if (textPartIndex >= 0) {
    const textPart = nextContent[textPartIndex]
    if (textPart.type === 'text') {
      nextContent[textPartIndex] = {
        ...textPart,
        text:
          textPart.text.trim().length > 0
            ? `${escapeHeaderShapedLines(textPart.text)}\n\n${pastedTextBlock}`
            : pastedTextBlock,
      }
    }
  } else {
    nextContent.push({
      type: 'text',
      text: pastedTextBlock,
    })
  }

  return {
    ...message,
    content: nextContent as AppendMessage['content'],
  }
}

// Matches the block headers written by `mergePastedTextCardsIntoMessage`.
const CARD_HEADER_REGEX = new RegExp(
  `^\\[(${CARD_LABEL_PATTERN}) (\\d+): (.*)\\]$`,
)
const SELECTION_PLAYS_SUFFIX_REGEX =
  / @ (?:playbook:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|library:([a-z0-9_-]+))#(\S+)$/i
const SELECTION_FILE_SUFFIX_REGEX =
  / @ file:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

export interface UserMessageCardSegment {
  source: PastedTextMergeCard['source']
  title: string
  text: string
  locator?: string
  fileId?: UUID
  plays?: PlaysCitation
}

const parseSelectionHeaderTitle = (
  title: string,
): Pick<UserMessageCardSegment, 'title' | 'locator' | 'fileId' | 'plays'> => {
  const playsSuffix = title.match(SELECTION_PLAYS_SUFFIX_REGEX)
  if (playsSuffix && playsSuffix.index !== undefined)
    return {
      title: title.slice(0, playsSuffix.index),
      plays: {
        ...(playsSuffix[1]
          ? { promptId: playsSuffix[1] as UUID }
          : { librarySlug: playsSuffix[2] }),
        nodeIds: uniq(playsSuffix[3].split(',')),
      },
    }
  const fileSuffix = title.match(SELECTION_FILE_SUFFIX_REGEX)
  if (!fileSuffix || fileSuffix.index === undefined) return { title }

  const titleWithoutFile = title.slice(0, fileSuffix.index)
  const fileId = fileSuffix[1] as UUID
  const locatorSeparatorIndex = titleWithoutFile.lastIndexOf(' @ ')
  if (locatorSeparatorIndex === -1) return { title: titleWithoutFile, fileId }

  const locator = titleWithoutFile.slice(locatorSeparatorIndex + 3).trim()
  try {
    parseLocator(locator)
  } catch (error) {
    if (!(error instanceof LocatorParseError)) throw error
    return { title: titleWithoutFile, fileId }
  }

  return {
    title: titleWithoutFile.slice(0, locatorSeparatorIndex),
    locator,
    fileId,
  }
}

export interface UserMessageSegments {
  /** The text the user actually typed (everything before the first card). */
  text: string
  cards: UserMessageCardSegment[]
}

// The merge joins blocks with '\n\n', so exactly one trailing blank line on a
// segment is separator, not content.
const stripOneSeparatorLine = (segmentLines: string[]): string => {
  if (segmentLines.length > 0 && segmentLines[segmentLines.length - 1] === '') {
    return segmentLines.slice(0, -1).join('\n')
  }
  return segmentLines.join('\n')
}

/**
 * Inverse of `mergePastedTextCardsIntoMessage` for display: splits a sent
 * user message back into the typed text and its pasted/playbook card blocks
 * so the chat bubble can render cards instead of the full inlined text.
 *
 * Headers must carry sequential ordinals starting at 1 — the shape the merge
 * emits — and the merge escapes header-shaped lines of typed and card text,
 * so only its own headers split the message. A message without a `1`-ordinal
 * header passes through untouched.
 */
export const splitUserMessageCards = (message: string): UserMessageSegments => {
  const lines = message.split('\n')
  const cards: UserMessageCardSegment[] = []
  const typedLines: string[] = []
  let currentHeader: Omit<UserMessageCardSegment, 'text'> | null = null
  let currentBody: string[] = []

  // Only a card followed by another card carries a separator line.
  const pushCurrent = (separated: boolean) => {
    if (!currentHeader) return
    const body = currentBody.map(unescapeHeaderShapedLine)
    cards.push({
      ...currentHeader,
      text: separated ? stripOneSeparatorLine(body) : body.join('\n'),
    })
  }

  for (const line of lines) {
    const header = line.match(CARD_HEADER_REGEX)
    // Only accept the ordinal the merge would have written next; anything
    // else is content that merely looks like a header.
    if (
      header &&
      Number(header[2]) === cards.length + (currentHeader ? 2 : 1)
    ) {
      pushCurrent(true)
      const source = sourceOfLabel(header[1])
      currentHeader = {
        source,
        ...(source === 'selection'
          ? parseSelectionHeaderTitle(header[3])
          : { title: header[3] }),
      }
      currentBody = []
      continue
    }
    if (currentHeader) {
      currentBody.push(line)
    } else {
      typedLines.push(line)
    }
  }
  pushCurrent(false)

  if (cards.length === 0) {
    // No cards: the message is returned byte-for-byte untouched.
    return { text: message, cards }
  }

  return {
    text: stripOneSeparatorLine(typedLines.map(unescapeHeaderShapedLine)),
    cards,
  }
}

/**
 * The identity a selection chip carries for active-state tracking: the same
 * capture, wherever it renders (staged in the composer, sent in a bubble),
 * lights up as one chip while its highlight is on screen in the viewer.
 */
export const selectionChipSignature = (
  fileId: UUID,
  locator: string | null | undefined,
  text: string,
): string => `${fileId}|${locator ?? ''}|${text}`
